import React, { useState, Suspense, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import trTR from 'antd/es/locale/tr_TR';
import { ConfigProvider } from 'antd';
import {
  Tooltip,
  Row,
  Button,
  Table,
  Col,
  Form,
  Tag,
  Dropdown,
  Image,
} from 'antd';
import {
  FilterOutlined,
  EditOutlined,
  MoreOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import debounce from 'lodash/debounce';
import {
  debouncedDataLoadWaitMs,
  ORDER_TYPES,
} from '../../../../helpers/constants';
import { CSVLink } from 'react-csv';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import { getOrderItemProperties } from '../../../../store/requests/order';

const Edit = React.lazy(() => import('./Edit'));
const GrammageProductsFilter = React.lazy(
  () => import('./GrammageProductsFilter'),
);
let lastQuery = false;

const GrammageProducts = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };
  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = 'filter=active==true';

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item]) {
          queryString += ` and ${item === 'productName' ? 'name' : item}=='${encodeURI(filters[item])}'`;
        }
      });
    }

    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (queryString !== lastQuery && !loading) {
      lastQuery = queryString;
      setLoading(true);
      getOrderItemProperties(queryString)
        .then((response) => {
          const { _embedded, page } = response;
          const { orderItemPropertiesDtoList } = _embedded ? _embedded : [];
          setRows(orderItemPropertiesDtoList ? orderItemPropertiesDtoList : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );
  const columns = [
    {
      title: t('productImage'),
      dataIndex: 'imageUrl',
      sorter: true,
      showSorterTooltip: false,
      render: (imageUrl) => (
        <Image
          width={50}
          src={
            imageUrl
              ? imageUrl
              : 'https://images.ceptesok.com/cdn-cgi/image/width=49,height=49,fit=pad,quality=100/logos/service-types/market-logo.svg'
          }
        />
      ),
    },
    {
      title: t('productCode'),
      dataIndex: 'productCode',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('productName'),
      dataIndex: 'name',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('excessTotal'),
      dataIndex: 'isMaximumQuantityLimitEnabled',
      sorter: true,
      showSorterTooltip: false,
      render: (excessTotal) =>
        excessTotal ? (
          <Tag color="green">{t('open')}</Tag>
        ) : (
          <Tag color="red">{t('close')}</Tag>
        ),
    },
    {
      title: t('overCollectionPercentage'),
      dataIndex: 'maximumQuantityRate',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('missingTotal'),
      dataIndex: 'isMinimumQuantityLimitEnabled',
      sorter: true,
      showSorterTooltip: false,
      render: (missingTotal) =>
        missingTotal ? (
          <Tag color="green">{t('open')}</Tag>
        ) : (
          <Tag color="red">{t('close')}</Tag>
        ),
    },
    {
      title: t('underCollectionPercentage'),
      dataIndex: 'minimumQuantityRate',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const actions = [
    {
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={'OrderItemProperties.csv'}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [
              item.productCode,
              item.name,
              item.isMaximumQuantityLimitEnabled,
              item.maximumQuantityRate,
              item.isMinimumQuantityLimitEnabled,
              item.minimumQuantityRate,
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.customContent ?? (
            <Button
              type={item?.type}
              shape="round"
              icon={item?.icon}
              onClick={item.onClick}
              disabled={item.disabled}
              style={{ marginBottom: '10px', marginRight: '3px' }}
              danger={item?.danger}
            />
          )}
        </Tooltip>
      );
    });
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <GrammageProductsFilter
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
        {isEditModalOpen && (
          <Edit
            orderItemProperty={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default connect()(GrammageProducts);
